//
// Copyright (c) 2025 Comcast
//

import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useMemo } from 'react'
import { EditorContext } from '@tiptap/react'

import { Toolbar as TipTapToolbar, ToolbarGroup } from '@tiptap-ui/components/tiptap-ui-primitive/toolbar'
import { Spacer } from '@tiptap-ui/components/tiptap-ui-primitive/spacer'
import { HeadingDropdownMenu } from '@tiptap-ui/components/tiptap-ui/heading-dropdown-menu'
import { MarkButton } from '@tiptap-ui/components/tiptap-ui/mark-button'
import { LinkPopover } from '@tiptap-ui/components/tiptap-ui/link-popover'
import { ListButton } from '@tiptap-ui/components/tiptap-ui/list-button'
import { CodeBlockButton } from '@tiptap-ui/components/tiptap-ui/code-block-button'
import { BlockquoteButton } from '@tiptap-ui/components/tiptap-ui/blockquote-button'
import { TextColorPopover } from '@tiptap-ui/components/tiptap-ui/text-color-popover'
import { TableDropdownMenu } from '@tiptap-ui/components/tiptap-ui/table-dropdown-menu'
import { UndoRedoButton } from '@tiptap-ui/components/tiptap-ui/undo-redo-button'

import { HorizontalRuleButton } from './HorizontalRuleButton'

const StyledTipTapToolbar = styled(TipTapToolbar)`
  padding-bottom: 8px;
`

const Toolbar = ({ editor }) => {
  const value = useMemo(() => ({ editor }), [editor])
  const editor_ = value.editor

  if (!editor_) {
    return null
  }

  return (
    <EditorContext.Provider value={{ editor: editor_ }}>
      <StyledTipTapToolbar variant='default'>
        <ToolbarGroup>
          <HeadingDropdownMenu levels={[1, 2, 3, 4, 5, 6]} />
          <MarkButton type='strong' tooltip='Bold' />
          <MarkButton type='em' tooltip='Italic' />
          <MarkButton type='underline' />
          <MarkButton type='strike' />
          <MarkButton type='subsup' attrs={{ type: 'sup' }} tooltip='Superscript' />
          <MarkButton type='subsup' attrs={{ type: 'sub' }} tooltip='Subscript' />
        </ToolbarGroup>

        <Spacer size='8px' />

        <ToolbarGroup>
          <TextColorPopover />
          <HorizontalRuleButton editor={editor_} />
          <ListButton type='bulletList' showShortcut={false} />
          <ListButton type='orderedList' showShortcut={false} />
          <BlockquoteButton />
          <MarkButton type='code' />
          <CodeBlockButton />
          <LinkPopover />
          <TableDropdownMenu />
        </ToolbarGroup>

        <Spacer />

        <ToolbarGroup>
          <UndoRedoButton action='undo' />
          <UndoRedoButton action='redo' />
        </ToolbarGroup>
      </StyledTipTapToolbar>
    </EditorContext.Provider>
  )
}

Toolbar.propTypes = {
  editor: PropTypes.object.isRequired,
}

export default Toolbar
